/**
 * @file api.ts
 * @description API Client Type Definitions for Task 18.2
 *
 * This file provides TypeScript types for the API client layer:
 * - ApiClientConfig: Configuration for creating axios instances
 * - ApiError: Standardized error shape for consistent error handling
 * - TypedApiClient: Typed extension of AxiosInstance
 * - Interceptor types: Request/Response/Error interceptor function signatures
 *
 * Reference: .taskmaster/docs/research/task-18-api-services-typescript-migration.md
 * Section 2.2.1
 */

import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from 'axios';

/**
 * Default timeout in milliseconds for API requests
 * Used by apiClient.ts when creating the axios instance
 */
export const API_DEFAULT_TIMEOUT_MS = 30000;

/**
 * Configuration for creating the API client instance
 */
export interface ApiClientConfig {
  /** Base URL for all API requests (e.g., 'https://api.example.com') */
  baseURL: string;
  /** Request timeout in milliseconds (default: API_DEFAULT_TIMEOUT_MS) */
  timeout?: number;
  /** Default headers to include with every request */
  headers?: Record<string, string>;
}

/**
 * Standardized API error shape for consistent error handling across services
 *
 * This interface provides a uniform error structure that can be used to:
 * - Display user-friendly error messages
 * - Log errors with consistent metadata
 * - Handle specific error codes programmatically
 */
export interface ApiError {
  /** HTTP status code (e.g., 401, 404, 500) */
  status: number;
  /** Error code from backend (e.g., 'ENTRY_FEE_PAYMENT_REQUIRED', 'VALIDATION_ERROR') */
  code?: string;
  /** Human-readable error message */
  message: string;
  /** Original error data from response (for debugging or detailed error handling) */
  data?: unknown;
}

/**
 * Type guard for ApiError
 *
 * Use this to narrow unknown errors to ApiError in catch blocks:
 * ```typescript
 * try {
 *   await apiClient.get('/resource');
 * } catch (error) {
 *   if (isApiError(error)) {
 *     console.log(error.status, error.message);
 *   }
 * }
 * ```
 */
export const isApiError = (error: unknown): error is ApiError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as ApiError).status === 'number'
  );
};

/**
 * Request interceptor function type
 *
 * Used to modify request config before sending (e.g., adding auth headers)
 */
export type RequestInterceptor = (
  config: AxiosRequestConfig
) => AxiosRequestConfig | Promise<AxiosRequestConfig>;

/**
 * Response interceptor function type
 *
 * Used to transform or inspect responses before they reach the caller
 */
export type ResponseInterceptor = (
  response: AxiosResponse
) => AxiosResponse | Promise<AxiosResponse>;

/**
 * Error interceptor function type
 *
 * Used to handle errors globally (e.g., refresh tokens, logout on 401)
 */
export type ErrorInterceptor = (error: AxiosError) => Promise<never>;

/**
 * Typed API client type alias for AxiosInstance
 *
 * AxiosInstance already supports generic type parameters for HTTP methods:
 *
 * ```typescript
 * interface UserResponse { id: number; name: string; }
 *
 * const response = await apiClient.get<UserResponse>('/users/me');
 * // response.data is typed as UserResponse
 * ```
 *
 * This type alias provides semantic clarity when typing apiClient exports
 * and ensures consistency across the codebase.
 */
export type TypedApiClient = AxiosInstance;

/**
 * Helper to create an ApiError from an AxiosError
 *
 * Useful for converting Axios errors into the standardized ApiError format
 */
export const toApiError = (error: AxiosError<{ code?: string; message?: string }>): ApiError => {
  return {
    status: error.response?.status ?? 0,
    code: error.response?.data?.code,
    message: error.response?.data?.message ?? error.message ?? 'Unknown error',
    data: error.response?.data,
  };
};
